import React, { JSX, useState, useRef, useEffect } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { processCoverImage } from "../../lib/imageUtils";
import "./BoardSettingsModal.css";

type Member = {
  id: string;
  display_name: string;
  avatar_url?: string;
};

type BoardSettingsModalProps = {
  open: boolean;
  onClose: () => void;
  boardName: string;
  currentCoverUrl: string | null;
  boardId: string;
  inviteCode: string;
  onUpdate: (name: string, coverUrl: string | null) => void;
  onRequestLeave: () => void;
  initialMembers?: Member[];
};

export default function BoardSettingsModal({
  open,
  onClose,
  boardName: initialBoardName,
  currentCoverUrl,
  boardId,
  inviteCode,
  onUpdate,
  onRequestLeave,
  initialMembers = []
}: BoardSettingsModalProps): JSX.Element | null {
  const { user } = useAuth();
  const [boardName, setBoardName] = useState(initialBoardName);
  const [coverPreview, setCoverPreview] = useState<string | null>(currentCoverUrl);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [members, setMembers] = useState<Member[]>(initialMembers);

  // Sync with initialMembers prop when it updates
  useEffect(() => {
    if (initialMembers.length > 0) {
      setMembers(initialMembers);
    }
  }, [initialMembers]);

  // Fallback fetch if initialMembers not provided
  useEffect(() => {
    if (open && boardId && initialMembers.length === 0 && members.length === 0) {
      const fetchMembers = async () => {
        // First fetch user_ids from board_members
        const { data: memberData, error: memberError } = await supabase
          .from('board_members')
          .select('user_id')
          .eq('board_id', boardId);

        if (memberError) {
          console.error('Error fetching board members:', memberError);
          return;
        }

        const userIds = memberData.map(m => m.user_id);

        if (userIds.length > 0) {
          // Then fetch profiles for these users
          const { data: profilesData, error: profilesError } = await supabase
            .from('profiles')
            .select('id, display_name, avatar_url')
            .in('id', userIds);

          if (profilesError) {
            console.error('Error fetching member profiles:', profilesError);
          } else {
            setMembers(profilesData || []);
          }
        } else {
          setMembers([]);
        }
      };

      fetchMembers();
    }
  }, [open, boardId, initialMembers.length, members.length]);

  useEffect(() => {
    if (open) {
      setBoardName(initialBoardName);
      setCoverPreview(currentCoverUrl);
      setSelectedFile(null);
    }
  }, [open, initialBoardName, currentCoverUrl]);

  // Clean up object URL to prevent memory leaks
  useEffect(() => {
    return () => {
      if (coverPreview && coverPreview !== currentCoverUrl) {
        URL.revokeObjectURL(coverPreview);
      }
    };
  }, [coverPreview, currentCoverUrl]);

  if (!open) return null;

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const originalFile = e.target.files[0];
      setCoverPreview(URL.createObjectURL(originalFile));
      const processedFile = await processCoverImage(originalFile);
      setSelectedFile(processedFile);
    }
  };

  const handleRemoveImage = () => {
    setCoverPreview(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const saveChanges = async () => {
    if (!boardId) return;

    // Only save if dirty (optional optimization, but good for UX)
    // For now we save always to match request "automatically save"

    try {
      setLoading(true);
      let finalCoverUrl = currentCoverUrl;

      // Check if image was removed
      if (!selectedFile && !coverPreview) {
        finalCoverUrl = null;
      }

      // 1. Upload new image if selected
      if (selectedFile) {
        // Always use .jpg since processCoverImage converts to JPEG
        const fileName = `${boardId}/${Math.random()}.jpg`;

        const { error: uploadError } = await supabase.storage
          .from('board-covers')
          .upload(fileName, selectedFile);

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from('board-covers')
          .getPublicUrl(fileName);

        finalCoverUrl = urlData.publicUrl;
      }

      // 2. Update board details
      const { error: updateError } = await supabase
        .from('boards')
        .update({
          name: boardName,
          cover_image_url: finalCoverUrl
        })
        .eq('id', boardId);

      if (updateError) throw updateError;

      // 3. Fire-and-forget preload (non-blocking)
      if (finalCoverUrl && finalCoverUrl !== currentCoverUrl) {
        const img = new Image();
        img.src = finalCoverUrl;
        // No await here - let it download in background while we close modal
      }

      // 4. Notify parent immediately
      onUpdate(boardName, finalCoverUrl);

    } catch (error) {
      console.error('Error updating board:', error);
      // Silent fail or alert? Alerting might be annoying on close, but better than silent data loss.
      alert('Failed to save changes.');
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveBoardClick = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    onRequestLeave();
  };



  const handleClose = async () => {
    // Save before closing
    await saveChanges();
    onClose();
  };

  return (
    <>
      <div className="board-settings-modal">
        <div className="overlay" onClick={handleClose}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2 className="modal-title">Edit Board</h2>
              </div>
              <button className="modal-close-button" onClick={handleClose}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="settings-form">
              <div className="form-group">
                <label className="label">Cover Image</label>
                <div
                  className="upload-area"
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    backgroundImage: coverPreview ? `url(${coverPreview})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    border: coverPreview ? '2px solid transparent' : '2px dashed #e2e8f0'
                  }}
                >
                  {!coverPreview && (
                    <>
                      <svg className="upload-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="upload-text">Upload an image</span>
                    </>
                  )}
                  {coverPreview && (
                    <button
                      type="button"
                      className="remove-image-button"
                      onClick={(e) => { e.stopPropagation(); handleRemoveImage(); }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  )}
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageSelect}
                  accept="image/*"
                  style={{ display: 'none' }}
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label className="label">Board Name</label>
                <input
                  className="input"
                  value={boardName}
                  onChange={(e) => setBoardName(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label className="label">Invite Code</label>
                <div style={{ position: 'relative' }}>
                  <input
                    className="input"
                    value={inviteCode}
                    readOnly
                    style={{ backgroundColor: '#f3f4f6', color: '#6b7280', cursor: 'default' }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(inviteCode);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: copied ? '#10b981' : '#137fec',
                      fontWeight: 600,
                      fontSize: '12px',
                      cursor: 'pointer',
                      transition: 'color 0.2s'
                    }}
                  >
                    {copied ? 'COPIED!' : 'COPY'}
                  </button>
                </div>
              </div>



            </div>

            {/* Members List Section */}
            <div className="members-section">
              <h3 className="members-section-title">Members</h3>
              <div className="members-list">
                {members.length === 0 ? (
                  <div style={{ color: '#6b7280', fontSize: '14px', fontStyle: 'italic' }}>Loading members...</div>
                ) : (
                  members.map(member => (
                    <div key={member.id} className="member-item">
                      <div className="member-avatar">
                        {member.avatar_url ? (
                          <img src={member.avatar_url} alt={member.display_name} />
                        ) : (
                          <span>{member.display_name?.charAt(0).toUpperCase() || '?'}</span>
                        )}
                      </div>
                      <span className="member-name">{member.display_name || 'Unknown User'}</span>
                      {user?.id === member.id && (
                        <button
                          type="button"
                          className="leave-board-small-button"
                          onClick={handleLeaveBoardClick}
                          title="Leave Board"
                        >
                          Leave
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

    </>
  );
}
